<template>
  <div class="page-container user-statistic-page">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>数据管理</el-breadcrumb-item>
      <el-breadcrumb-item>数据报表</el-breadcrumb-item>
      <el-breadcrumb-item>用户数据统计表</el-breadcrumb-item>
    </el-breadcrumb>
    <header class="page-heading">
      <h2>用户数据统计表</h2>
      <p>
        查看用户资金流入、交易与返佣数据；金额截取保留 4 位小数，末尾为 0 时隐藏
      </p>
    </header>
    <el-tabs v-model="activeTab" @tab-click="changeTab">
      <el-tab-pane label="时间段汇总" name="summary" />
      <el-tab-pane label="每日明细" name="daily" />
    </el-tabs>
    <section class="filter-panel">
      <el-form
        :inline="true"
        label-position="top"
        @submit.native.prevent="search"
      >
        <el-form-item >
          <el-input
            v-model.trim="filters.uid"
            placeholder="请输入UID"
            clearable
            @keyup.enter.native="search"
          />
        </el-form-item>
        <el-form-item >
          <el-input
            v-model.trim="filters.partnerUid"
            placeholder="请输入合伙人UID"
            clearable
            @keyup.enter.native="search"
          />
        </el-form-item>
        <el-form-item >
          <el-select v-model="filters.userType" placeholder="全部" clearable>
            <el-option label="普通用户" :value="2" />
            <el-option label="模拟用户" :value="1" />
          </el-select>
        </el-form-item>
        <el-form-item >
          <el-date-picker
            v-model="filters.dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期（必填）"
            end-placeholder="结束日期（必填）"
            :clearable="false"
            unlink-panels
          />
        </el-form-item>
        <el-form-item class="action-item">
          <el-button type="primary" @click="search">查询</el-button>
          <el-button @click="reset">重置</el-button>
        </el-form-item>
        <el-form-item class="export-item">
          <el-button
            type="primary"
            plain
            :loading="exporting"
            @click="exportData"
            >导出数据</el-button
          >
        </el-form-item>
      </el-form>
    </section>
    <div class="summary-grid">
      <div
        v-for="field in summaryFields"
        :key="field.prop"
        class="summary-card"
      >
        <span>总{{ field.label }}</span>
        <strong :class="amountClass(totals[field.prop])">{{
          formatValue(totals[field.prop], field)
        }}</strong>
      </div>
    </div>
    <section class="table-panel">
      <div class="table-heading">
        <div>
          <h3>
            {{ activeTab === "daily" ? "每日明细数据" : "时间段汇总数据" }}
          </h3>
          <p>
            共 {{ columns.length }} 个字段 · 当前展示
            {{ visibleColumns.length }} 个
          </p>
        </div>
        <el-button icon="el-icon-setting" @click="settingsVisible = true"
          >列设置</el-button
        >
      </div>
      <el-alert
        v-if="!loading && !connected"
        title="统计接口请求失败，请稍后重试"
        type="info"
        :closable="false"
        show-icon
      />
      <el-table
        :key="activeTab + '-' + columnVersion"
        ref="table"
        v-loading="loading"
        :data="rows"
        border
        height="450"
        class="statistic-table"
      >
        <el-table-column
          v-for="(column, columnIndex) in visibleColumns"
          :key="column.prop"
          :fixed="columnIndex < (activeTab === 'daily' ? 2 : 1) ? 'left' : false"
          :prop="column.prop"
          :label="column.label"
          :min-width="column.minWidth"
          show-overflow-tooltip
        >
          <template slot-scope="scope">
            <span
              :class="
                column.type === 'amount'
                  ? amountClass(scope.row[column.prop])
                  : ''
              "
            >
              {{ formatValue(scope.row[column.prop], column) }}
            </span>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-row">
        <span>共 {{ total }} 条</span>
        <el-pagination
          :current-page="page"
          :page-size="size"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="sizes, prev, pager, next, jumper"
          @current-change="changePage"
          @size-change="changeSize"
        />
      </div>
    </section>
    <column-settings-dialog
      :visible.sync="settingsVisible"
      :columns="columns"
      report-title="用户数据统计表"
      :page-label="activeTab === 'daily' ? '每日明细页' : '汇总页'"
      @save="saveSettings"
    />
  </div>
</template>

<script>
import Decimal from "decimal.js";
import dayjs from "dayjs";
import ColumnSettingsDialog from "../UserContractAssetMonitor/components/ColumnSettingsDialog";
import {
  restoreColumns,
  saveColumns,
} from "../UserContractAssetMonitor/columns";
import {
  COLUMNS,
  STORAGE_PREFIX,
  SUMMARY_FIELDS,
} from "./columns";
import api from "./api";

function defaults() {
  return {
    uid: "",
    partnerUid: "",
    userType: "",
    dateRange: [
      dayjs().subtract(7, "day").format("YYYY-MM-DD"),
      dayjs().format("YYYY-MM-DD"),
    ],
  };
}

export default {
  name: "UserDataStatistic",
  components: { ColumnSettingsDialog },
  data() {
    return {
      activeTab: "summary",
      filters: defaults(),
      page: 1,
      size: 10,
      rows: [],
      total: 0,
      totals: {},
      connected: false,
      loading: false,
      exporting: false,
      settingsVisible: false,
      columnVersion: 0,
      columnSets: {
        summary: restoreColumns(
          STORAGE_PREFIX + "summary",
          COLUMNS.filter((column) => column.prop !== "statisticsDate"),
        ),
        daily: restoreColumns(STORAGE_PREFIX + "daily", COLUMNS),
      },
    };
  },
  computed: {
    columns() {
      return this.columnSets[this.activeTab];
    },
    visibleColumns() {
      return this.columns.filter((column) => column.visible);
    },
    summaryFields() {
      return SUMMARY_FIELDS;
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    buildQuery(includePage = true) {
      const [startDate, endDate] = this.filters.dateRange || [];
      const query = {
        uid: this.filters.uid || null,
        partnerUid: this.filters.partnerUid || null,
        userType: this.filters.userType || null,
        startDate: startDate || null,
        endDate: endDate || null,
      };
      if (includePage)
        Object.assign(query, { page: this.page, size: this.size });
      return query;
    },
    buildExportQuery() {
      return {
        ...this.buildQuery(false),
        // 后端按 prop 白名单生成 Excel，顺序与当前页面列顺序一致。
        exportFields: this.visibleColumns.map((column) => column.prop),
      };
    },
    validateDateRange() {
      const dateRange = this.filters.dateRange;
      if (
        Array.isArray(dateRange) &&
        dateRange.length === 2 &&
        dateRange[0] &&
        dateRange[1]
      ) {
        return true;
      }
      this.$message.warning("请选择完整的统计时间");
      return false;
    },
    summaryValues(data) {
      return {
        contractFee: data.totalContractFee,
        fundingFee: data.totalFundingFee,
        contractClosedPnl: data.totalContractClosePnl,
        contractNetPnl: data.totalContractNetPnl,
        bonusFeeDeductAmount: data.totalExperienceFeeDeductAmount,
        bonusFundingDeductAmount: data.totalExperienceFundingFeeDeductAmount,
        bonusPnlDeductAmount: data.totalExperiencePnlDeductAmount,
        feeCouponDeductAmount: data.totalFeeCouponDeductAmount,
        pnlCouponDeductAmount: data.totalPnlCouponDeductAmount,
        contractNetPnlAfterDeduct: data.totalDeductedContractNetPnl,
        actualRebateAmount: data.totalActualRebateAmount,
        contractNetPnlAfterRebate: data.totalRebateAfterContractNetPnl,
        positionCouponProfit: data.totalPositionExperienceProfit,
        contractDeductFee: data.totalContractDeductFee,
      };
    },
    async loadData() {
      if (!this.validateDateRange()) return;
      const requestedTab = this.activeTab;
      this.loading = true;
      try {
        const daily = requestedTab === "daily";
        const [listResponse, summaryResponse] = await Promise.all([
          (daily ? api.getDailyList : api.getSummaryList)(this.buildQuery()),
          (daily ? api.getDailySummary : api.getSummary)(
            this.buildQuery(false),
          ),
        ]);
        if (requestedTab !== this.activeTab) return;
        if (listResponse.code !== 0 || summaryResponse.code !== 0)
          throw new Error("统计接口返回失败");
        const listData = listResponse.data || {};
        this.rows = Array.isArray(listData.list) ? listData.list : [];
        this.total = Number(listData.totalCount) || 0;
        this.totals = this.summaryValues(summaryResponse.data || {});
        this.connected = true;
      } catch (error) {
        if (requestedTab !== this.activeTab) return;
        this.rows = [];
        this.total = 0;
        this.totals = {};
        this.connected = false;
      } finally {
        if (requestedTab === this.activeTab) this.loading = false;
        this.$nextTick(() => {
          if (this.$refs.table) this.$refs.table.doLayout();
        });
      }
    },
    search() {
      if (!this.validateDateRange()) return;
      this.page = 1;
      this.loadData();
    },
    reset() {
      this.filters = defaults();
      this.search();
    },
    changeTab() {
      this.settingsVisible = false;
      this.search();
    },
    changePage(page) {
      this.page = page;
      this.loadData();
    },
    changeSize(size) {
      this.size = size;
      this.search();
    },
    saveSettings(columns) {
      const nextColumns = columns.map((column) => ({ ...column }));
      this.$set(this.columnSets, this.activeTab, nextColumns);
      saveColumns(nextColumns, STORAGE_PREFIX + this.activeTab);
      // Element UI 会缓存 el-table-column，变更key可确保保存后立即按新顺序重新注册列。
      this.columnVersion += 1;
      this.$nextTick(() => {
        if (this.$refs.table) this.$refs.table.doLayout();
      });
    },
    formatValue(value, column) {
      if (value === null || value === undefined || value === "") return "--";
      if (column.type === "amount") {
        try {
          return new Decimal(value)
            .toDecimalPlaces(4, Decimal.ROUND_DOWN)
            .toString();
        } catch (error) {
          return "--";
        }
      }
      if (column.prop === "userType") {
        return Number(value) === 1
          ? "模拟用户"
          : Number(value) === 2
          ? "普通用户"
          : value;
      }
      return value;
    },
    amountClass(value) {
      if (value === null || value === undefined || value === "") return "";
      try {
        const amount = new Decimal(value);
        return amount.isZero()
          ? ""
          : amount.isNegative()
          ? "amount-negative"
          : "amount-positive";
      } catch (error) {
        return "";
      }
    },
    async exportData() {
      if (!this.validateDateRange()) return;
      if (!this.visibleColumns.length) {
        this.$message.warning("请至少选择一个列表字段后再导出");
        return;
      }
      this.exporting = true;
      try {
        const daily = this.activeTab === "daily";
        const data = await (daily ? api.exportDaily : api.exportSummary)(
          this.buildExportQuery(),
        );
        const blob =
          data instanceof Blob
            ? data
            : new Blob([data], { type: "application/vnd.ms-excel" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const timestamp = dayjs().format("YYYYMMDDHHmmss");
        const dataType = daily ? "每日明细数据" : "时间段汇总数据";
        link.download = `用户数据统计表${dataType}${timestamp}.xls`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.user-statistic-page {
  padding: 18px;
  background: #f4f7fb;
  min-height: calc(100vh - 80px);
  box-sizing: border-box;
}
.page-heading {
  margin: 28px 0 16px;
  h2 {
    margin: 0 0 7px;
    font-size: 24px;
    color: #1f2a3d;
  }
  p {
    margin: 0;
    color: #8793a6;
    font-size: 13px;
  }
}
.filter-panel,
.table-panel {
  background: #fff;
  border: 1px solid #dfe6f1;
  border-radius: 9px;
  padding: 18px;
}
.filter-panel {
  margin-bottom: 16px;
  ::v-deep .el-form {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 10px;
  }
  ::v-deep .el-form-item {
    margin: 0;
  }
  ::v-deep .el-form-item__label {
    padding: 0 0 6px;
    line-height: 20px;
    font-size: 12px;
    color: #738096;
  }
  ::v-deep .el-input {
    width: 180px;
  }
  ::v-deep .el-select .el-input {
    width: 140px;
  }
  ::v-deep .el-date-editor {
    width: 300px;
  }
}
.export-item {
  margin-left: auto !important;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}
.summary-card {
  background: #fff;
  border: 1px solid #e5eaf2;
  border-radius: 6px;
  padding: 14px;
  min-width: 0;
  span {
    display: block;
    font-size: 12px;
    line-height: 20px;
    color: #8793a6;
  }
  strong {
    display: block;
    font-size: 18px;
    font-weight: 500;
    margin-top: 7px;
    overflow-wrap: anywhere;
  }
}
.table-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  h3 {
    margin: 0 0 6px;
    font-size: 17px;
    color: #283447;
  }
  p {
    margin: 0;
    color: #8793a6;
    font-size: 12px;
  }
}
.statistic-table {
  margin-top: 12px;
  width: 100%;
  ::v-deep th {
    background: #f7f9fc;
    color: #65728a;
    font-weight: 400;
  }
}
.pagination-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 18px;
  color: #7d899e;
  font-size: 13px;
}
.amount-negative {
  color: #ff626a;
}
.amount-positive {
  color: #25b78a;
}
@media (max-width: 1500px) {
  .summary-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
@media (max-width: 900px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .pagination-row {
    flex-wrap: wrap;
    gap: 12px;
  }
}
</style>
